import React from "react";

/**
 * Shared chart language for the reports dashboard.
 *
 * Palette: the validated categorical set, assigned in fixed order.
 * Verified on the white chart surface with the adjacent-pair list
 * (donut segments, legend rows, bars):
 *   lightness band PASS · chroma floor PASS
 *   CVD separation PASS  (worst adjacent dE 9.1 protan)
 *   normal-vision   PASS  (worst adjacent dE 19.6)
 *   contrast        WARN  -> relief supplied: every series carries a visible
 *                           direct label (the legend prints each count), so
 *                           identity is never colour-alone.
 * Re-run before changing any hex:
 *   node scripts/validate_palette.js "<hex,...>" --mode light --surface "#ffffff"
 */
export const SERIES = [
  "#2a78d6", // 1 blue
  "#eb6834", // 2 orange
  "#1baf7a", // 3 aqua
  "#eda100", // 4 yellow
  "#e87ba4", // 5 magenta
  "#008300", // 6 green
  "#4a3aa7", // 7 violet
  "#e34948", // 8 red
];

export const INK = {
  primary: "#1c2333",
  secondary: "#49536b",
  muted: "#7a8194",
  grid: "#eff1f6",
  border: "#e8eaf0",
  surface: "#ffffff",
  guide: "#94a3b8", // recessive reference/ideal line
};

/**
 * Colour follows the entity, not its position: a status keeps its hue even when
 * other statuses are added or filtered away. Known workflow names get a fixed
 * slot; anything a project invents is assigned deterministically by name.
 */
const FIXED_SLOT = {
  "to do": 0,
  "in progress": 1,
  "code review": 2,
  "in review": 2,
  "in testing": 3,
  qa: 3,
  "production ready": 4,
  done: 5,
};

export const statusColor = (name = "") => {
  const key = String(name).trim().toLowerCase();
  if (key in FIXED_SLOT) return SERIES[FIXED_SLOT[key]];
  // stable hash so a custom status keeps one colour across renders
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) % 997;
  const spare = [6, 7, 1, 3];
  return SERIES[spare[hash % spare.length]];
};

/** Recessive axis defaults shared by the bar and line charts. */
export const axisProps = {
  tick: { fill: INK.muted, fontSize: 12 },
  tickLine: false,
  axisLine: { stroke: INK.grid },
};

export const gridProps = {
  stroke: INK.grid,
  strokeDasharray: "4 4",
  horizontal: true,
  vertical: false,
};

/** One tooltip card for every chart on the dashboard. */
export function ChartTooltip({ active, payload, label, valueSuffix = "" }) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div
      style={{
        background: INK.surface,
        border: `1px solid ${INK.border}`,
        borderRadius: 12,
        boxShadow:
          "0 4px 10px rgba(17,24,39,0.06), 0 18px 36px -22px rgba(17,24,39,0.3)",
        padding: "10px 12px",
        minWidth: 140,
      }}
    >
      {label != null && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: INK.muted,
            letterSpacing: "0.01em",
            marginBottom: 6,
          }}
        >
          {label}
        </div>
      )}
      {payload.map((entry) => (
        <div
          key={entry.dataKey || entry.name}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: INK.primary,
            lineHeight: 1.7,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              flex: "0 0 auto",
              background: entry.color || entry.payload?.fill || INK.muted,
            }}
          />
          <span style={{ color: INK.secondary }}>{entry.name}</span>
          <strong style={{ marginLeft: "auto", fontWeight: 650 }}>
            {entry.value}
            {valueSuffix}
          </strong>
        </div>
      ))}
    </div>
  );
}

/** Card shell so every panel on the dashboard matches the rest of the app. */
export const cardSx = {
  backgroundColor: INK.surface,
  border: `1px solid ${INK.border}`,
  borderRadius: "15px",
  boxShadow: "0 1px 2px rgba(17,24,39,0.04)",
  transition:
    "border-color .28s cubic-bezier(.2,.8,.2,1), box-shadow .28s cubic-bezier(.2,.8,.2,1)",
  "&:hover": {
    borderColor: "#dfe3ec",
    boxShadow:
      "0 1px 2px rgba(17,24,39,0.04), 0 10px 24px -16px rgba(17,24,39,0.22)",
  },
};

export const cardTitleSx = {
  fontSize: "12.5px !important",
  fontWeight: "600 !important",
  color: "#59637a",
  textTransform: "uppercase",
  letterSpacing: "0.01em",
};
